import { useState } from "react";
import { useNavigate } from "react-router-dom";
import "../styles/SummaryUpdatesPage.css";
import { useAuth } from "../context/AuthContext";
import { useMatter } from "../context/MatterContext";
import { useModules } from "../context/ModulesContext";
import { generateBhpRetainer } from "../helpers/apiHelper";

const DEFAULT_NEXT_PATH = "/dashboard";
const RETAINER_PENDING_KEY = "bhp_retainer_pending";
const RETAINER_RETURN_PATH_KEY = "bhp_retainer_return_path";
const DEFAULT_RETAINER_RETURN_PATH = "/summary-updates";

export default function SummaryUpdatesPage({ nextPath = DEFAULT_NEXT_PATH }) {
  const navigate = useNavigate();
  const { tokens } = useAuth();
  const { party, matter } = useMatter();
  const { refreshModules } = useModules();

  const [acknowledged, setAcknowledged] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const getToken = () =>
    tokens?.idToken ||
    tokens?.accessToken ||
    localStorage.getItem("idToken") ||
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("idToken") ||
    sessionStorage.getItem("accessToken") ||
    "";

  const getPartyId = () =>
    party?.partyId ||
    party?.id ||
    party?.Id ||
    localStorage.getItem("clientId") ||
    sessionStorage.getItem("clientId") ||
    "";

  const getMatterId = () =>
    matter?.id ||
    matter?.matterId ||
    matter?.Id ||
    "";

  const markRetainerPendingPersistently = () => {
    try {
      localStorage.setItem(RETAINER_PENDING_KEY, "true");
      localStorage.setItem(
        RETAINER_RETURN_PATH_KEY,
        DEFAULT_RETAINER_RETURN_PATH,
      );
    } catch (e) {
      console.error("Failed to persist retainer pending flag:", e);
    }
  };

  const clearRetainerPendingPersistently = () => {
    try {
      localStorage.removeItem(RETAINER_PENDING_KEY);
      localStorage.removeItem(RETAINER_RETURN_PATH_KEY);
    } catch (e) {
      console.error("Failed to clear retainer pending flag:", e);
    }
  };

  const handleContinue = async () => {
    if (!acknowledged || loading) return;

    setError("");

    const token = getToken();
    const partyId = getPartyId();
    const matterId = getMatterId();

    if (!token) {
      setError("Sessão expirada. Faça login novamente.");
      return;
    }

    if (!partyId) {
      setError("Não foi possível identificar seu perfil. Tente novamente.");
      return;
    }

    if (!matterId) {
      setError("Não foi possível identificar seu caso. Tente novamente.");
      return;
    }

    setLoading(true);
    try {
      await generateBhpRetainer(token, partyId, matterId);
      await refreshModules();
      clearRetainerPendingPersistently();
      navigate(nextPath, { replace: true });
    } catch (err) {
      console.error(err);
      setError(err?.message || "Não foi possível confirmar os termos agora.");
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    if (loading) return;

    setError("");
    setLoading(true);

    try {
      markRetainerPendingPersistently();
    } finally {
      setLoading(false);
      navigate(nextPath, { replace: true });
    }
  };

  return (
    <div className="su-page">
      <header className="su-header">
        <div className="su-headerInner">
          <div className="su-titleBlock">
            <h1 className="su-title">Resumo das atualizações</h1>
            <p className="su-subtitle">Leia com atenção antes de continuar.</p>
          </div>
        </div>
      </header>

      <main className="su-main">
        <section className="su-card" aria-labelledby="summary-updates-title">
          <div className="su-cardHeader">
            <h2 id="summary-updates-title" className="su-cardTitle">
              Resumo das atualizações
            </h2>
            <p className="su-cardHint">
              Este texto explica, em alto nível, as alterações propostas.
            </p>
          </div>

          <div
            className="su-content"
            role="region"
            aria-label="Conteúdo do resumo"
          >
            <ol>
              <li>
                Embora não haja nada de errado com seu contrato atual ou com as
                últimas instruções que você nos deu, gostaríamos que você
                confirmasse que continuamos autorizados a representá-lo em sua
                ação.
              </li>
              <li>
                Precisamos deixar mais claro que o acordo coletivo de honorários
                de êxito (o "CCFA") é um documento independente.
              </li>
              <li>
                Precisamos revisar o CCFA e o acordo de gerenciamento de
                litígios (o "LMA") para refletir corretamente a gestão das
                ações.
              </li>
              <li>
                Queremos garantir que os Réus não possam contestar os contratos
                com base em fundamentos técnicos da lei do consumidor inglesa.
              </li>
              <li>
                Garantir que quaisquer valores recuperados no Brasil sejam
                considerados danos para os fins do CCFA.
              </li>
            </ol>

            <h3>Resumo em linguagem simples</h3>

            <p>
              <a
                href="https://assets.pogustgoodhead.com/documents/project_r/PlainLanguageSummaryPT.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                Abrir resumo em linguagem simples (PDF)
              </a>
            </p>

            <h3>Documentos atualizados</h3>

            <ul className="su-linksList">
              <li>
                <a
                  href="https://assets.pogustgoodhead.com/documents/project_r/CCFAPT.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Contrato Coletivo de Honorários em caso de Êxito
                </a>
              </li>
              <li>
                <a
                  href="https://assets.pogustgoodhead.com/documents/project_r/LMAPT.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Contrato de Gestão de Litígios
                </a>
              </li>
              <li>
                <a
                  href="https://assets.pogustgoodhead.com/documents/project_r/StandardMarianaTermsOfBusinessPT.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Termos de Prestação de Serviço
                </a>
              </li>
            </ul>
          </div>

          {error && (
            <div className="su-error" role="alert">
              {error}
            </div>
          )}

          <div className="su-ack">
            <label className="su-checkbox">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
              />
              <span>
                Li e compreendi todas as informações contidas nos documentos
                acima e aceito os termos neles contidos.
              </span>
            </label>

            <button
              type="button"
              className={`su-continue ${acknowledged ? "isEnabled" : "isDisabled"}`}
              onClick={handleContinue}
              disabled={!acknowledged || loading}
            >
              {loading ? "Processando..." : "Confirmar e continuar"}
            </button>

            <button
              type="button"
              className="su-continue"
              onClick={handleSkip}
              disabled={loading}
            >
              {loading ? "Processando..." : "Ver mais tarde"}
            </button>
          </div>
        </section>
      </main>
    </div>
  );
}
